import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnboardingProgress } from '../../hooks/useOnboardingProgress';
import { ONBOARDING_STEPS } from '../../types/onboardingTypes';
import { useAuth } from '../../context/AuthContext';
import { LanguageSelector } from '../LanguageSelector/LanguageSelector';
import { BrandLogo } from '../common/BrandLogo';
import WelcomeStep from './steps/WelcomeStep';
import CompanyNameStep from './steps/CompanyNameStep';
import AddressStep from './steps/AddressStep';
import CommercialRegisterStep from './steps/CommercialRegisterStep';
import BankDetailsStep from './steps/BankDetailsStep';
import TaxNumberStep from './steps/TaxNumberStep';
import LogoStep from './steps/LogoStep';
import CompletionStep from './steps/CompletionStep';
import { IconClose } from '../../icons';

interface OnboardingWizardProps {
  onClose?: (startTour?: boolean) => void;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onClose }) => {
  const { t } = useTranslation('onboarding');
  const { saveSellerData } = useAuth();
  const {
    currentStep,
    progress,
    isLoading,
    interviewData,
    goToNextStep,
    goToPreviousStep,
    skipStep,
    saveStepData,
    completeOnboarding,
    completionPercentage,
    totalSteps,
    currentStepIndex,
    shouldShowStep,
    isCorporation,
  } = useOnboardingProgress();

  // Handler für Sprachwechsel - speichert Sprache in Firebase
  const handleLanguageChange = async (langCode: string) => {
    try {
      await saveSellerData({ preferredLanguage: langCode });
      console.log('Language preference saved to Firebase:', langCode);
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  };

  const [isAnimating, setIsAnimating] = useState(true);

  // Entry animation
  useEffect(() => {
    const timer = setTimeout(() => setIsAnimating(false), 100);
    return () => clearTimeout(timer);
  }, []);

  // Get filtered steps based on conditions
  const filteredSteps = ONBOARDING_STEPS.filter(step => shouldShowStep(step.id));

  // Einziger Handler zum Abschluss des Onboardings - mit oder ohne Tour
  const handleFinish = async (startTour: boolean) => {
    await completeOnboarding();
    onClose?.(startTour);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-surface">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-primary text-lg">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'welcome':
        return <WelcomeStep onNext={goToNextStep} />;
      
      case 'companyName':
        return (
          <CompanyNameStep
            initialValue={interviewData.name || ''}
            onSave={saveStepData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
          />
        );
      
      case 'address':
        return (
          <AddressStep
            initialData={{
              street: interviewData.street || '',
              zip: interviewData.zip || '',
              city: interviewData.city || '',
              country: interviewData.country || 'DE',
            }}
            onSave={saveStepData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
          />
        );
      
      case 'commercialRegister':
        return (
          <CommercialRegisterStep
            initialData={{
              commercialRegisterNumber: interviewData.commercialRegisterNumber || null,
              commercialRegisterLocation: interviewData.commercialRegisterLocation || null,
            }}
            companyName={interviewData.name || ''}
            onSave={saveStepData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            onSkip={skipStep}
          />
        );
      
      case 'bankDetails':
        return (
          <BankDetailsStep
            initialData={{
              iban: interviewData.bankDetails?.iban || '',
              bic: interviewData.bankDetails?.bic || '',
              bankName: interviewData.bankDetails?.bankName || '',
              accountHolder: interviewData.bankDetails?.accountHolder || '',
            }}
            onSave={saveStepData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            onSkip={skipStep}
          />
        );
      
      case 'taxNumber':
        return (
          <TaxNumberStep
            initialData={{
              taxNumber: interviewData.taxNumber || null,
              vatNumber: interviewData.vatNumber || null,
              isSmallBusiness: interviewData.isSmallBusiness,
            }}
            isCorporation={isCorporation}
            onSave={saveStepData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            onSkip={skipStep}
          />
        );
      
      case 'logo':
        return (
          <LogoStep
            initialLogoUrl={interviewData.logoUrl || null}
            onSave={saveStepData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            onSkip={skipStep}
          />
        );
      
      case 'completion':
        return (
          <CompletionStep
            companyName={interviewData.name || ''}
            onFinish={handleFinish}
          />
        );
      
      default:
        return <WelcomeStep onNext={goToNextStep} />;
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex flex-col bg-surface transition-opacity duration-300 ${
        isAnimating ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Header with Progress */}
      <header className="flex-shrink-0 border-b border-hairline bg-[rgb(var(--surface-base-rgb)/0.95)] backdrop-blur-sm relative z-[60]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            {/* Logo */}
            <div className="flex items-center">
              <BrandLogo className="h-8" />
            </div>
            
            {/* Step Indicator */}
            <div className="hidden sm:flex items-center text-sm text-muted">
              <span className="text-primary font-medium">{currentStepIndex + 1}</span>
              <span className="mx-1">/</span>
              <span>{totalSteps}</span>
            </div>
            
            {/* Language Selector & Skip/Close Button */}
            <div className="flex items-center space-x-2">
              {/* Language Selector - immer sichtbar */}
              <LanguageSelector onLanguageChange={handleLanguageChange} />
              
              {/* Skip/Close Button (only show after welcome) */}
              {currentStep !== 'welcome' && currentStep !== 'completion' && (
                <button
                  onClick={() => onClose?.(false)}
                  className="p-2 text-muted hover:text-primary transition-colors rounded-lg hover:bg-surface-raised"
                  title={t('common.close')}
                >
                  <IconClose size={20} />
                </button>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative">
            <div className="h-1.5 bg-surface-raised rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 to-[#0C1322] rounded-full transition-all duration-500 ease-out"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
            
            {/* Step Dots */}
            <div className="absolute -top-1 left-0 right-0 flex justify-between">
              {filteredSteps.map((step, index) => {
                const isCompleted = progress?.completedSteps.includes(step.id);
                const isCurrent = step.id === currentStep;
                
                return (
                  <div
                    key={step.id}
                    className={`
                      w-3.5 h-3.5 rounded-full border-2 transition-all duration-300
                      ${isCompleted 
                        ? 'bg-indigo-500 border-indigo-500' 
                        : isCurrent 
                          ? 'bg-surface border-indigo-500 scale-125' 
                          : 'bg-surface-raised border-hairline'
                      }
                    `}
                    title={t(`steps.${step.id}.title`)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow overflow-y-auto relative z-0">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
          {renderCurrentStep()}
        </div>
      </main>

      {/* Footer with Tips */}
      <footer className="flex-shrink-0 border-t border-hairline bg-[rgb(var(--surface-base-rgb)/0.95)] backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <p className="text-center text-faint text-sm">
            💡 {t('footer.tip')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default OnboardingWizard;

